import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import Button from '../Button.js';
import ImageButton from '../ImageButton.js';
import SearchStorage from './SearchStorage';
import CGSearch from './CGSearch.js';
import './SearchBarSettings.css';

const SearchSettings = ({ tokens, isFocused, searchID, onSavedSearchSelected, onDrawerClosed }) => {
  const [savedSearches, setSavedSearches] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const closeTimeout = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(isFocused || tokens.length > 0 || showDrawer || showHelp);

  useEffect(() => {
    setSavedSearches(SearchStorage.get(searchID));
  }, [tokens, isFocused, searchID]);

  useEffect(() => {
    if (isFocused || tokens.length > 0 || showDrawer || showHelp) {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
      setSettingsOpen(true);
    } else {
      closeTimeout.current = setTimeout(() => {
        setSettingsOpen(false);
      }, 1000);
    }
    return () => {
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, [isFocused, tokens.length, showDrawer, showHelp]);

  const savedSearchCount = savedSearches.length;
  // const currentSearch = CGSearch.stringifyTokens(tokens);
  // const currentSearch = CGSearch.stringifyTokens(tokens.filter(t => t.raw.trim() !== ''));
  let currentSearch = CGSearch.stringifyTokens(tokens.filter(t => t.raw.trim() !== ''));
  currentSearch = replaceEntities(currentSearch);
  const saveDisabled = tokens.length === 0 || savedSearches.includes(currentSearch) || currentSearch.trim() === '';

  // Class names for styling
  const searchSettingsClass = classNames('search-settings', {
    'search-settings-open': settingsOpen,
  });
  const drawerClass = classNames('saved-searches', {
    'saved-searches-open': showDrawer,
  });
  const helpClass = classNames('search-help', {
    'search-help-open': showHelp,
  });
  const savedWithCountClass = classNames('saved-with-count', {
    'saved-with-count-active': showDrawer,
  });
  const helpWithIconClass = classNames('saved-with-count', {
    'saved-with-count-active': showHelp,
  });

  function replaceEntities(str) {
    return str.replace(/&gt;/g, '>')
              .replace(/&lt;/g, '<')
              .replace(/&ge;/g, '>=')
              .replace(/&le;/g, '<=')
  }

  function onSaveSearch() {
    // const searchString = CGSearch.stringifyTokens(tokens);
    let searchString = CGSearch.stringifyTokens(tokens);
    searchString = replaceEntities(searchString);
    SearchStorage.add(searchID, searchString);
    setSavedSearches(SearchStorage.get(searchID));
  }

  return (
    <>
      <div className={searchSettingsClass}>
        <Button height={18} disabled={saveDisabled} onClick={onSaveSearch}>
          Save
        </Button>
        <Button
          height={18}
          width="140px"
          disabled={savedSearchCount === 0}
          onClick={() => {
            const newValue = !showDrawer;
            setShowDrawer(newValue);
            setShowHelp(false);
            if (!newValue) onDrawerClosed();
          }}
        >
          <div className={savedWithCountClass}>
            <div>Saved Searches</div>
            <div className="saved-count-badge">{savedSearchCount}</div>
            { showDrawer && (
              <ImageButton
                imageName='sidebarClose'
                size='small'
                title='Back to All Columns'
                onClick={() => {
                  setShowDrawer(false);
                  onDrawerClosed();
                }}
              />
            )}
          </div>
        </Button>
        <Button className={helpWithIconClass} height={18} onClick={() => {
          const newValue = !showHelp;
          setShowHelp(newValue);
          setShowDrawer(false);
          if (!newValue) onDrawerClosed();
        }}>
          <div>Help</div>
          { showHelp && (
            <ImageButton
              imageName='sidebarClose'
              size='small'
              title='Close Help'
              onClick={() => {
                setShowHelp(false);
                onDrawerClosed();
              }}
            />
          )}
        </Button>
      </div>
      <div className={drawerClass}>
        {savedSearches.map((search, idx) => (
          <div
            key={idx}
            className="saved-search-row"
          >
            <div
              onClick={() => {onSavedSearchSelected(search)}}
              style={{width: '100%'}}
            >
              {search}
            </div>
            <ImageButton
              imageName="delete"
              title="Delete Saved Search"
              onClick={() => {
                SearchStorage.remove(searchID, search);
                setSavedSearches(SearchStorage.get(searchID));
              }}
            />
          </div>
        ))}
        { savedSearches.length > 1 && (
            <ImageButton
              imageName="delete"
              text="Delete All"
              width={100}
              className="delete-all-saved-searches"
              title="Delete All Saved Search"
              onClick={() => {
                if (window.confirm("Are you sure you want to delete all saved searches?")) {
                  SearchStorage.clear(searchID);
                  setSavedSearches(SearchStorage.get(searchID));
                }
              }}
            />
        )}
      </div>
              
      <div className={helpClass}>
        <h3>Creating and Editing Search Terms</h3>
        <ul style={{margin: 0, paddingLeft: 20}}>
          <li>Start typing or click the <ImageButton inline imageName='add' size='small' /> button (on the right) to create a new search term.</li>
          <li>Click above a term (<div className='token-example token-column'>any</div>) to edit its settings (e.g. logic, modifier, or attribute restriction).</li>
          <li>By default, the search applies to any attribute. You can restrict it to specific attributes.</li>
          <li>Nested attributes (e.g. qualifiers and metadata) can be accessed by clicking buttons labeled with a  <ImageButton inline imageName='forward' size='small' />.</li>
          <li>
            Use logic buttons (<strong>AND</strong>, <strong>OR</strong>, <strong>NOT</strong>) to control how terms are combined:
            <ul style={{ paddingLeft: 18 }}>
              <li><strong>NOT</strong>: Must <em>not</em> include this term</li>
              <li><strong>AND</strong>: Must include this term</li>
              <li><strong>OR</strong>: Must include one of the OR terms</li>
            </ul>
          </li>
          <li>
            Apply modifiers to refine matches:
            <ul style={{ paddingLeft: 18 }}>
              <li><strong>For numbers</strong>: &lt;, &le;, =, &ge;, &gt;</li>
              <li><strong>For strings</strong>: contains, starts with, ends with, equals</li>
            </ul>
          </li>
        </ul>
        <br />
        <hr />
        <div>
          <h3>Saved Searches</h3>
          <p>
            You can save search queries for easy reuse later. Saved searches are specific to the browser, device, and search bar where they were created.
          </p>
          <p>
            Below the search bar, you'll find two buttons:
          </p>
          <ul style={{ paddingLeft: 18 }}>
            <li><strong>Save</strong> – Saves the current query. This button is disabled if the search is empty or already saved.</li>
            <li><strong>Saved Searches</strong> – Opens a list of your saved queries. A small badge shows how many are currently saved. This button is disabled if there are no saved searches.</li>
          </ul>

          <p>
            Clicking a saved query will replace your current search. You can remove saved queries by clicking the trash icon next to each one.
          </p>
        </div>
        <br />
        <hr />
        <div>
          <h3>Text-Based Query Syntax</h3>
          <p>
            Search queries can be entered directly as plain text using the following syntax:
          </p>
          <pre><code>[Logic][Modifier]search_term[Modifier]:attribute_name</code></pre>

          <h3>Logic Operators</h3>
          <ul>
            <li><strong>(blank)</strong> — must include one of the these terms (<strong>OR</strong>)</li>
            <li><strong>+</strong> — must include this term (<strong>ANT</strong>)</li>
            <li><strong>!</strong> — must not include this term (<strong>NOT</strong>)</li>
          </ul>

          <h3>Search Modifiers</h3>
          <p><strong>For numbers:</strong></p>
          <ul>
            <li><code>&lt;</code>, <code>&le;</code>, <code>=</code>, <code>&ge;</code>, <code>&gt;</code></li>
          </ul>
          <p><strong>For strings:</strong></p>
          <ul>
            <li><strong>(blank)</strong> — contains</li>
            <li><code>=search_term</code> — equals</li>
            <li><code>search_term*</code> — starts with</li>
            <li><code>*search_term</code> — ends with</li>
          </ul>

          <h3>Query Examples</h3>
          <ul>
            <li><code>+&gt;10:score</code> — matches items with a score greater than 10</li>
            <li><code>!*virus:name</code> — excludes items whose name ends with "virus"</li>
            <li><code>=exact_name:type</code> — matches items where the type equals "exact_name"</li>
          </ul>

          <h3>Additional Notes</h3>
          <ul>
            <li>
              Multiple search terms are separated by spaces
            </li>
            <li>
              If a search term contains whitespace, wrap it in double quotes.<br />
              <em>Example:</em> <code>"ribosomal protein":function</code>
            </li>
            <li>
              For nested attributes, use additional <code>:</code> separators to specify the full path.<br />
              <em>Example:</em> <code>=kinase:meta:category</code>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default SearchSettings;